// Effective-date + status-proximity formatter for the variation table's
// Effective column. The bottom-row meta is designed to
// combine status with proximity, e.g.:
//
//   approved · effective in 4 weeks
//   proposed 6 days ago · effective in 13 weeks
//   rejected · would have been effective 2 months ago
//
// Status transitions are user-driven, not date-driven. A variation that
// is still proposed past its effective date is not auto-approved — the
// proposed-for-N-days cue surfaces the slippage so the contract manager
// can act, but never mutates the stored state.

import { humaniseDays } from "../../../../utils/formatCoverage";
import { formatDayMonthYear } from "../../../../utils/formatShortDate";
import { getBrisbaneToday } from "../../../../utils/getBrisbaneToday";
import type { VariationStatus } from "../../../../types";

export type VariationDateTone =
  | "muted"
  | "neutral"
  | "success"
  | "brand"
  | "danger";

export interface FormattedVariationDate {
  /** `15 Apr 2026`. */
  effective: string;
  /** Combined status + proximity meta-line. */
  meta: string;
  tone: VariationDateTone;
}

function fmtDay(iso: string): string {
  return formatDayMonthYear(iso);
}

function isoDayUTC(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function daysBetween(fromIso: string, toIso: string): number {
  return Math.round((isoDayUTC(toIso) - isoDayUTC(fromIso)) / 86_400_000);
}

export interface FormatVariationDateInput {
  /** YYYY-MM-DD ISO date. Optional because Proposed variations can sit
   *  in negotiation without a finalised effective date — the backend
   *  only enforces the presence at /approve. When undefined or empty,
   *  the formatter renders an em-dash for the date and a status-only
   *  meta line (no proximity cue). */
  effectiveDate?: string;
  status: VariationStatus;
  /** ISO timestamps from the variation record — used to anchor the
   *  "proposed N days ago" / "approved N days ago" meta. Optional
   *  because backfilled records may not have these populated. */
  approvedAt?: string;
  rejectedAt?: string;
  /** ISO date for the proposal — backend persists this on the standard
   *  createdAt timestamp. */
  proposedAt?: string;
  /** Brisbane-today override for tests. */
  today?: string;
}

function isoFromTimestamp(ts?: string): string | undefined {
  if (!ts) return undefined;
  // Backend persists timestamps as ISO with full date+time. Take the
  // YYYY-MM-DD prefix for date arithmetic; precision below day is
  // irrelevant for "N days ago" rounding.
  return ts.slice(0, 10);
}

export function formatVariationDate({
  effectiveDate,
  status,
  approvedAt,
  rejectedAt,
  proposedAt,
  today = getBrisbaneToday(),
}: FormatVariationDateInput): FormattedVariationDate {
  // Missing-date branch: a proposed variation may not yet have an
  // effective date. We render an em-dash + status-only meta so the
  // table row stays legible without a proximity cue. Without this
  // guard `daysBetween` calls `.split` on undefined and crashes the
  // entire register surface.
  const hasDate =
    typeof effectiveDate === "string" && effectiveDate.trim() !== "";
  if (!hasDate) {
    return formatVariationDateNoEffective({
      status,
      approvedAt,
      rejectedAt,
      proposedAt,
      today,
    });
  }
  const effective = fmtDay(effectiveDate);
  const daysToEffective = daysBetween(today, effectiveDate);
  const effectiveLabel =
    daysToEffective > 0
      ? `effective in ${humaniseDays(daysToEffective)}`
      : daysToEffective < 0
        ? `effective ${humaniseDays(-daysToEffective)} ago`
        : "effective today";

  if (status === "approved") {
    const approvedDay = isoFromTimestamp(approvedAt);
    if (approvedDay) {
      const daysSinceApproved = daysBetween(approvedDay, today);
      if (daysSinceApproved >= 0) {
        return {
          effective,
          meta: `approved ${humaniseDays(daysSinceApproved)} ago · ${effectiveLabel}`,
          tone: "success",
        };
      }
    }
    return { effective, meta: `approved · ${effectiveLabel}`, tone: "success" };
  }

  if (status === "rejected") {
    const rejectedDay = isoFromTimestamp(rejectedAt);
    if (rejectedDay) {
      const daysSinceRejected = daysBetween(rejectedDay, today);
      if (daysSinceRejected >= 0) {
        return {
          effective,
          meta: `rejected ${humaniseDays(daysSinceRejected)} ago`,
          tone: "muted",
        };
      }
    }
    return { effective, meta: "rejected", tone: "muted" };
  }

  // Proposed branch — combine "proposed N days ago" with the effective-
  // proximity cue. A variation that has been proposed for a long time is
  // visibly different from a freshly-proposed one (per brief §5).
  const proposedDay = isoFromTimestamp(proposedAt);
  const daysSinceProposed = proposedDay
    ? daysBetween(proposedDay, today)
    : null;

  // Decision-required tone: amber-equivalent. We use "brand" because the
  // shared cell-tone palette doesn't expose amber directly; consumers can
  // map "brand" to amber-like for proposed surfaces if they wish.
  const tone: VariationDateTone = "brand";

  if (daysSinceProposed === null || daysSinceProposed < 0) {
    return { effective, meta: `proposed · ${effectiveLabel}`, tone };
  }

  return {
    effective,
    meta: `proposed ${humaniseDays(daysSinceProposed)} ago · ${effectiveLabel}`,
    tone,
  };
}

interface NoEffectiveInput {
  status: VariationStatus;
  approvedAt?: string;
  rejectedAt?: string;
  proposedAt?: string;
  today: string;
}

/** Status-only meta line for variations without an effective date.
 *  Rendered when the user creates a Proposed variation and leaves the
 *  date unset — the row still needs to render without crashing the
 *  register. The em-dash for `effective` matches the brand convention
 *  for "no value yet". */
function formatVariationDateNoEffective({
  status,
  approvedAt,
  rejectedAt,
  proposedAt,
  today,
}: NoEffectiveInput): FormattedVariationDate {
  const effective = "—";

  if (status === "approved") {
    const approvedDay = isoFromTimestamp(approvedAt);
    if (approvedDay) {
      const daysSinceApproved = daysBetween(approvedDay, today);
      if (daysSinceApproved >= 0) {
        return {
          effective,
          meta: `approved ${humaniseDays(daysSinceApproved)} ago · effective date not set`,
          tone: "success",
        };
      }
    }
    return {
      effective,
      meta: "approved · effective date not set",
      tone: "success",
    };
  }

  if (status === "rejected") {
    const rejectedDay = isoFromTimestamp(rejectedAt);
    if (rejectedDay) {
      const daysSinceRejected = daysBetween(rejectedDay, today);
      if (daysSinceRejected >= 0) {
        return {
          effective,
          meta: `rejected ${humaniseDays(daysSinceRejected)} ago`,
          tone: "muted",
        };
      }
    }
    return { effective, meta: "rejected", tone: "muted" };
  }

  const proposedDay = isoFromTimestamp(proposedAt);
  const daysSinceProposed = proposedDay
    ? daysBetween(proposedDay, today)
    : null;
  const tone: VariationDateTone = "brand";
  if (daysSinceProposed === null || daysSinceProposed < 0) {
    return {
      effective,
      meta: "proposed · effective date not set",
      tone,
    };
  }
  return {
    effective,
    meta: `proposed ${humaniseDays(daysSinceProposed)} ago · effective date not set`,
    tone,
  };
}
